import React from 'react';
import PropTypes from 'prop-types';

import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import get from 'lodash/get';

import ExpirationDate from '@src/custom/expiration-date';
import CopyURLButton from './copy-attachment-url-button';
import RemoveButton from './remove-attachment-button';
import EditButton from './edit-attachment-button';

const StyledDetails = styled('div')({
    display: 'flex',
    flexDirection: 'column',
});

const StyledContent = styled(CardContent)({
    flex: '1 0 auto',
});

const StyledThumbnailContainer = styled('div')({
    position: 'relative',
});

const StyledThumbnail = styled(CardMedia)({
    height: '100%',
    width: '100%',
    minHeight: '8rem',
});

const StyledThumbnailExpiration = styled('div')({
    backgroundColor: '#ffffffCC',
    fontSize: '0.8rem',
    position: 'absolute',
    textAlign: 'center',
    width: '100%',
});

const StyledActions = styled(CardActions)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    paddingLeft: typeof theme?.spacing === 'function' ? theme.spacing(1) : '8px',
    paddingBottom: typeof theme?.spacing === 'function' ? theme.spacing(1) : '8px',
}));

const AttachmentItem = ({
    attachment,
    hideThumbnail,
    hideButtons,
    onAttachmentRemoved,
    onAttachmentUpdated,
}) => {
    const thumbnailURL = get(attachment, 'thumbnail.source');

    return (
        <Card variant="outlined">
            <Grid container sx={{ width: '100%' }} spacing={0}>
                {
                    thumbnailURL && !hideThumbnail ?
                        <StyledThumbnailContainer as={Grid} size={{ xs: 12, sm: 3, md: 2 }}>
                            <StyledThumbnailExpiration
                                as={ExpirationDate}
                                expiredAt={get(attachment, ['thumbnail', 'expired_at'])}
                            />
                            <StyledThumbnail
                                image={thumbnailURL}
                                title={get(attachment, 'thumbnail.alt')}
                            />
                        </StyledThumbnailContainer> :
                        null
                }
                <StyledDetails as={Grid} size={{ xs: 12, sm: hideThumbnail ? 12 : 9, md: hideThumbnail ? 12 : 10 }}>
                    <StyledContent>
                        <Typography component="h5" variant="h5">
                            { get(attachment, 'display_name') }
                        </Typography>
                        <Typography variant="subtitle1" color="textSecondary">
                            { get(attachment, 'filename') }
                        </Typography>
                        <ExpirationDate expiredAt={get(attachment, 'expired_at')} />
                    </StyledContent>
                    <StyledActions>
                        <CopyURLButton attachment={attachment} />
                        {
                            !hideButtons && (
                                <>
                                    <EditButton
                                        attachment={attachment}
                                        hideThumbnail={hideThumbnail}
                                        onUpdated={onAttachmentUpdated}
                                    />
                                    <RemoveButton
                                        attachment={attachment}
                                        onRemove={onAttachmentRemoved}
                                    />
                                </>
                            )
                        }
                    </StyledActions>
                </StyledDetails>
            </Grid>
        </Card>
    );
};

AttachmentItem.propTypes = {
    attachment: PropTypes.object.isRequired,
    hideThumbnail: PropTypes.bool,
    hideButtons: PropTypes.bool,
    onAttachmentRemoved: PropTypes.func.isRequired,
    onAttachmentUpdated: PropTypes.func.isRequired,
};

export default AttachmentItem;
